/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState, ReactNode } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';

// Material Dashboard 2 PRO React TS components
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import SidenavCollapse from "./SidenavCollapse";
import SidenavList from "./SidenavList";
import SidenavItem from "./SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "./SidenavRoot";
import sidenavLogoLabel from "./styles/sidenav";
import { useAuthProvider } from "../../../../providers";
import { ReactComponent as MenuOpenIcon } from '@cloudpremise/portal_core/src/themes/mui/assets/icons/menu_open_right.svg';


import {
    sidenavDesktopMenu,
} from "./styles/sidenav";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "../../context";

// Declaring props types for Sidenav
interface Props {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  brandName: string;
  routes: {
    [key: string]:
      | ReactNode
      | string
      | {
          [key: string]:
            | ReactNode
            | string
            | {
                [key: string]: ReactNode | string;
              }[];
        }[];
  }[];
  [key: string]: any;
}

function Sidenav({ color, brand, brandName, routes, ...rest }: Props): JSX.Element {
    const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
    const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, transparentNavbar } = controller;
    const location = useLocation();
    const { pathname } = location;
    const collapseName = pathname.split("/").slice(1)[0];
    const items = pathname.split("/").slice(1);
    const itemParentName = items[1];
    const itemName = items[items.length - 1];
    const [ authorized, authType ] = useAuthProvider();

    const pathnameCopy = pathname.replace(/^\/+/g, '');
    let isAdminSection = false;
    if(typeof(pathnameCopy) === "string" && pathnameCopy.split("/")[0] === "admin"){
      isAdminSection = true;
    }
  
    let textColor:
      | "primary"
      | "secondary"
      | "info"
      | "success"
      | "warning"
      | "error"
      | "dark"
      | "white"
      | "inherit"
      | "text"
      | "light" = "white";
  
    if (transparentSidenav || (whiteSidenav && !darkMode)) {
      textColor = "dark";
    } else if (whiteSidenav && darkMode) {
      textColor = "inherit";
    }
    const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
    // Styles for the navbar icons
    const iconsStyle = ({
        palette: { dark, white, text },
        functions: { rgba },
    }: {
        palette: any;
        functions: any;
    }) => ({
        color: () => {
            let colorValue = dark.main;
            if(darkMode){
              colorValue = white.main;
            }
            return colorValue;
        },
    });
  
    const closeSidenav = () => setMiniSidenav(dispatch, true);
  
    useEffect(() => {
      setOpenCollapse(collapseName);
      setOpenNestedCollapse(itemParentName);
    }, []);
  
    useEffect(() => {
      // A function that sets the mini state of the sidenav.
      function handleMiniSidenav() {
        setMiniSidenav(dispatch, window.innerWidth < 1200);
        setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
        // setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
      }
  
      /** 
       The event listener that's calling the handleMiniSidenav function when resizing the window.
      */
      window.addEventListener("resize", handleMiniSidenav);
  
      // Call the handleMiniSidenav function to set the state with the initial value.
      handleMiniSidenav();
  
      // Remove event listener on cleanup
      return () => window.removeEventListener("resize", handleMiniSidenav);
    }, [dispatch, location]);

    const isViewAllowed = (privacy) => {
        let allowed = true;
        switch(privacy){
            case 'private':
                allowed = authorized;
                break;
            case 'public':
                allowed = !authorized;
                break;
            case 'both':
                allowed = true;
                break;
            default:
                break;
        }
        return allowed;
    }
  
    // Render all the nested collapse items from the routes.js
    const renderNestedCollapse = (collapse: any) => {
      const template = collapse.map(({ name, route, key, href, privacy, icon, ...props }: any) => {
        if(!isViewAllowed(privacy)){
            return null;
        }
        return href ? (
            <Link
              key={key}
              href={href}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
              {...props}
            >
              <SidenavItem name={name} nested icon={icon} />
            </Link>
          ) : (
            <NavLink to={route+window.location.search} key={key} style={{ textDecoration: "none" }} {...props}>
              <SidenavItem name={name} icon={icon} active={route === pathname} nested />
            </NavLink>
        )
      });
  
      return template;
    };
    // Render the all the collpases from the routes.js
    const renderCollapse = (collapses: any) =>
      collapses.map(({ name, collapse, route, href, privacy, key, icon, ...props }: any) => {
        if(!isViewAllowed(privacy)){
            return null;
        }
        let returnValue;
  
        if (collapse) {
          returnValue = (
            <SidenavItem
              key={key}
              color={color}
              name={name}
              icon={icon}
              active={key === itemParentName ? "isParent" : false}
              open={openNestedCollapse === key}
              onClick={({ currentTarget }: any) =>
                openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                  ? setOpenNestedCollapse(false)
                  : setOpenNestedCollapse(key)
              }
            >
              {renderNestedCollapse(collapse)}
            </SidenavItem>
          );
        } else {
          returnValue = href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavItem color={color} name={name} icon={icon} active={key === itemName} nested />
            </Link>
          ) : (
            <NavLink to={route+window.location.search} key={key} style={{ textDecoration: "none" }} {...props}>
              <SidenavItem color={color} name={name} icon={icon} active={key === itemName} nested />
            </NavLink>
          );
        }
        return <SidenavList key={key}>{returnValue}</SidenavList>;
      });
  
    // Render all the routes from the routes.js (All the visible items on the Sidenav)
    const renderRoutes = routes.map(
      ({ type, name, icon, title, collapse, noCollapse, key, href, route, privacy, ...props }: any) => {
        if(!isViewAllowed(privacy)){
            return null;
        }
        let returnValue;
  
        if (type === "collapse") {
          if (href) {
            returnValue = (
              <Link
                href={href}
                key={key}
                target="_blank"
                rel="noreferrer"
                sx={{ textDecoration: "none" }}
                {...props}
              >
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  active={key === collapseName}
                  noCollapse={noCollapse}
                />
              </Link>
            );
          } else if (noCollapse && route) {
            returnValue = (
              <NavLink to={route+window.location.search} key={key} {...props}>
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  noCollapse={noCollapse}
                  active={key === collapseName}
                >
                  {collapse ? renderCollapse(collapse) : null}
                </SidenavCollapse>
              </NavLink>
            );
          } else {
            returnValue = (
              <SidenavCollapse
                key={key}
                name={name}
                icon={icon}
                active={key === collapseName}
                open={openCollapse === key}
                onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            );
          }
        } else if (type === "menu_item") {
          returnValue = (
              <NavLink to={route+window.location.search} key={key} style={{ textDecoration: "none" }} {...props}>
                  <SidenavItem color={color} icon={icon} name={name} active={key === itemName} />
              </NavLink>
          );
        } else if (type === "title") {
          returnValue = (
            <MDTypography
              key={key}
              color={textColor}
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
              pl={3}
              mt={2}
              mb={1}
              ml={1}
            >
              {title}
            </MDTypography>
          );
        } else if (type === "divider") {
          returnValue = (
            <Divider
              key={key}
              light={
                (!darkMode && !whiteSidenav && !transparentSidenav) ||
                (darkMode && !transparentSidenav && whiteSidenav)
              }
            />
          );
        }
  
        return returnValue;
      }
    );

    let menuContainerStyles = {};
    if(isAdminSection){
      menuContainerStyles = {
        // height: "calc(100vh - 250px)",
        overflowY: "auto",
        overflowX: "hidden"
      }
    }
  
    return (
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      >
        <MDBox sx={menuContainerStyles}>
          <MDBox pt={3} pb={1} px={2} textAlign="center" sx={{display: "flex", justifyContent: "space-between"}}>
              <MDBox>
                  <MDBox
                      display={{ xs: "block", xl: "none" }}
                      position="absolute"
                      top={0}
                      right={0}
                      p={1.625}
                      onClick={closeSidenav}
                      sx={{ cursor: "pointer" }}
                  >
                      <MDTypography variant="h6" color="secondary">
                      <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                      </MDTypography>
                  </MDBox>
                  <MDBox component={NavLink} to={"/home"+window.location.search} display="flex" alignItems="center">
                      {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
                      <MDBox
                      width={!brandName && "100%"}
                      sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}
                      >
                      <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
                          {brandName}
                      </MDTypography>
                      </MDBox>
                  </MDBox>
            </MDBox>
            {/* <IconButton
              sx={(theme) => sidenavDesktopMenu(theme, { transparentSidenav, whiteSidenav, miniSidenav, darkMode })}
              onClick={handleMiniSidenav}
              size="small"
              disableRipple
          >
              <Icon fontSize="medium" sx={iconsStyle}>
                {miniSidenav ? "menu" : "menu"}
              </Icon>
            </IconButton> */}
          </MDBox>
          {/* {
              miniSidenav ?
                  <> */}
                      <Divider
                          light={
                              (!darkMode && !whiteSidenav && !transparentSidenav) ||
                              (darkMode && !transparentSidenav && whiteSidenav)
                          }
                      />
                      <List>
                          <SidenavItem
                              color={color}
                              name="Minimize Sidebar"
                              icon={(
                                miniSidenav ?
                                  <span className="MuiIcon-root MuiIcon-fontSizeMedium">
                                    <MenuOpenIcon style={{width: "17px", fill: "currentColor"}} />
                                  </span>
                                :
                                  <Icon fontSize="medium" sx={iconsStyle}>
                                    {miniSidenav ? "menu" : "menu_open"}
                                  </Icon>
                              )}
                              active={false}
                              onClick={handleMiniSidenav}
                          />
                      </List>
                  {/* </>
              :
                  null
          } */}
          <Divider
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
          <List>{renderRoutes}</List>
        </MDBox>
        {
          isAdminSection ?
            <MDBox sx={{mt: "auto"}}>
              <List>
                <NavLink to={"/help"+window.location.search} style={{ textDecoration: "none" }} >
                  <SidenavItem color={"dark"} icon={<HelpOutlineOutlinedIcon />} name={"Help"} />
                </NavLink>
                <NavLink to={"/ask-athena"+window.location.search} style={{ textDecoration: "none" }} >
                  <SidenavItem color={"dark"} icon={<MenuOutlinedIcon />} name={"Ask Athena"} />
                </NavLink>
              </List>
            </MDBox>
          :
            null
        }
      </SidenavRoot>
    );
  }
  
  // Declaring default props for Sidenav
  Sidenav.defaultProps = {
    color: "info",
    brand: "",
  };
  
  export default Sidenav;
